// file: client/src/App.tsx
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import React from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { AuthProvider, useAuth } from "./hooks/use-auth";
import { ThemeProvider } from "./hooks/use-theme";
import { ErrorBoundary } from "./components/ErrorBoundary";
import LandingPage from "./pages/landing";
import NotFound from "./pages/not-found";
import Dashboard from "./pages/dashboard";
import Admin from "./pages/admin";
import AuthPage from "./pages/auth";
import CheckoutPage from "./pages/checkout";
import PricingPage from "./pages/pricing";
import StoryStudio from "./pages/story-studio";
import MyStoriesPage from "./pages/my-stories";
import StoryWorkspacePage from "./pages/story-workspace";
import StoryIdeaPage from "./pages/story-idea";
import TemplatesPage from "./pages/templates";
import AnalyticsPage from "./pages/analytics";
import SettingsPage from "./pages/settings";
import UpgradePage from "./pages/upgrade";
import ProfilePage from "./pages/profile";
import BillingPage from "./pages/billing";
import WriteStudio from "./writer/WriteStudio";
import StoryPreview from "./pages/StoryPreview";

/**
 * Redirects unauthenticated users to /auth, preserving the intended destination
 * so the auth page can redirect back after login.
 */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Don't redirect until we know the auth state — avoids flash-redirect on page load
    return null;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider>
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/story/preview/:shareId" element={<StoryPreview />} />

                {/* Protected routes — require login */}
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/story-idea" element={<PrivateRoute><StoryIdeaPage /></PrivateRoute>} />
                <Route path="/my-stories" element={<PrivateRoute><MyStoriesPage /></PrivateRoute>} />
                <Route path="/story-workspace/:id" element={<PrivateRoute><StoryWorkspacePage /></PrivateRoute>} />
                <Route path="/story-workspace" element={<PrivateRoute><StoryWorkspacePage /></PrivateRoute>} />
                <Route path="/story-studio" element={<PrivateRoute><StoryStudio /></PrivateRoute>} />
                <Route path="/templates" element={<PrivateRoute><TemplatesPage /></PrivateRoute>} />
                <Route path="/analytics" element={<PrivateRoute><AnalyticsPage /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                <Route path="/upgrade" element={<PrivateRoute><UpgradePage /></PrivateRoute>} />
                <Route path="/billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
                <Route path="/checkout" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
                <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;